import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { PayrollTimecardFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/payroll-timecard-feature-flags.enum';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { EffectiveDatesService } from 'src/app/travel-contracts/effective-dates/effective-dates.service';
import { BillingPolicy } from 'src/app/travel-contracts/effective-dates/model/billing-policy.model';

@UntilDestroy()
@Component({
    selector: 'effective-dates-summary.component',
    templateUrl: './effective-dates-summary.component.html',
    styleUrls: ['./effective-dates-summary.component.scss']
})
export class EffectiveDatesSummaryComponent implements OnInit {
    data = [];
    rateSections = ['Bill Rates'];
    ruleSections = ['Overtime Rules', 'Doubletime Rules'];
    startDateControl = new UntypedFormControl(new Date(), Validators.required);
    invalidDates = ['1900-01-01T00:00:00', '0001-01-01T00:00:00'];
    fieldsMapping = {
        billRegRate: 'Regular',
        billOverTimeRate: 'Overtime',
        billDoubleTimeRate: 'Doubletime',
        billChargeRate: 'Charge',
        billOnCallRate: 'On Call',
        billCallBackRate: 'Call Back',
        billHolidayRate: 'Holiday',
        billOnCall24HourRate: '24hr Call (Daily)',
        billNightCallRate: 'Night Call (Daily)',
        billWeekendRate: 'Weekend Call (Daily)',
        agencyRegPay: 'Regular',
        agencyOTPay: 'Overtime',
        agencyDTPay: 'Doubletime',
        agencyChargePay: 'Charge',
        agencyCallPay: 'On Call',
        agencyCBPay: 'Call Back',
        agencyHOLPay: 'Holiday',
        agencyOnCall24HourPay: '24hr Call (Daily)',
        agencyNightCallPay: 'Night Call (Daily)',
        agencyWeekendPay: 'Weekend Call (Daily)',
        billOverTimeDayRule: 'in a day',
        billOverTimeWeekRule: 'in a week',
        billOverTimeTwoWeekRule: 'in 2 weeks',
        billDoubleTimeDayRule: 'in a day',
        billDoubleTimeWeekRule: 'in a week',
        billDoubleTimeTwoWeekRule: 'in 2 weeks'
    };

    pendingSave: boolean;
    firsttimeSaveFromEffectiveDate = false;
    dateFormat = 'MM/DD/YYYY';
    displayLocumPayrollEffectiveDatesHistoryFlag = false;

    constructor(
        public dialogRef: MatDialogRef<EffectiveDatesSummaryComponent>,
        private readonly _effectiveDatesService: EffectiveDatesService,
        private readonly _toasterService: ToasterService,
        protected readonly _lDFeatureManager: LDFeatureManager,
        @Inject(MAT_DIALOG_DATA)
        public dialogData: {
            oldValue: any;
            newValue: any;
            isDisplayAgencyPay: boolean;
            contractId: number;
            policyId: number;
            startDate: Date;
            contractStartDate: string;
            contractEndDate: string;
            setEffectiveDate: boolean;
            effectiveDate: Date;
            isLocum: boolean;
        }
    ) {}

    ngOnInit(): void {
        this._lDFeatureManager
            .isEnabled(PayrollTimecardFeatureFlags.DisplayLocumPayrollEffectiveDatesHistory)
            .pipe(untilDestroyed(this))
            .subscribe((locumsFlagIsEnabled) => {
                this.displayLocumPayrollEffectiveDatesHistoryFlag = locumsFlagIsEnabled;
            });

        if (this.dialogData?.isDisplayAgencyPay) {
            this.rateSections.push('Agency Pay');
        }

        if (this.dialogData?.setEffectiveDate || this.invalidDates.includes(this.dialogData.startDate?.toString())) {
            this.startDateControl.patchValue(dayjs(this.dialogData.effectiveDate).toDate());
        }

        if (this.dialogData?.setEffectiveDate) {
            this.firsttimeSaveFromEffectiveDate = true;
        }

        if (this.dialogData.newValue) {
            for (const [key, value] of Object.entries(this.fieldsMapping)) {
                this.data.push({
                    payType: value,
                    billType: this.getBillType(key),
                    oldRate: this.dialogData.oldValue[key],
                    newRate: this.dialogData.newValue[key]
                });
            }
        }
    }

    get minDate() {
        return dayjs(this.dialogData.contractStartDate).toDate();
    }

    get maxDate() {
        return dayjs(this.dialogData.contractEndDate).toDate();
    }

    getBillType(val: string): string {
        if (val.startsWith('billOverTimeRate') || val.startsWith('billDoubleTimeRate')) {
            return 'bill';
        }
        if (val.startsWith('billOverTime')) {
            return 'overtime';
        }
        if (val.startsWith('billDoubleTime')) {
            return 'doubletime';
        }
        if (val.startsWith('bill')) {
            return 'bill';
        }
        if (val.startsWith('agency')) {
            return 'agency';
        }
        return '';
    }

    getRates(section: any) {
        const showLocums1099Rates = this.dialogData?.isLocum && this.displayLocumPayrollEffectiveDatesHistoryFlag;
        return this._effectiveDatesService.getRates(section, this.data, showLocums1099Rates);
    }

    updateBillingRate(contractId: number, data: BillingPolicy): Observable<BillingPolicy> {
        return this._effectiveDatesService.updateBillingRate(contractId, data);
    }

    addBillingRate(contractId: number, data: BillingPolicy): Observable<BillingPolicy> {
        return this._effectiveDatesService.addBillingRate(contractId, data);
    }

    saveChanges(): void {
        this.pendingSave = true;
        const dataToSave = {
            ...this.mapDataToUpdate(this.dialogData.newValue),
            startDate: this.startDateControl.value,
            id: this.dialogData.policyId
        };

        const obs$ =
            this.dialogData.policyId &&
            !this.firsttimeSaveFromEffectiveDate &&
            (dayjs(dataToSave.startDate).format(this.dateFormat) ===
                dayjs(this.dialogData.startDate).format(this.dateFormat) ||
                dayjs(dataToSave.startDate).format(this.dateFormat) ===
                    dayjs(this.dialogData.contractStartDate).format(this.dateFormat))
                ? this.updateBillingRate(this.dialogData.contractId, dataToSave)
                : this.addBillingRate(this.dialogData.contractId, dataToSave);
        obs$.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.close(true);
            },
            error: (err) => {
                this._toasterService.toast(err.error.error_description);
            },
            complete: () => {
                this.pendingSave = false;
            }
        });
    }

    mapDataToUpdate(data): BillingPolicy {
        return {
            ...data,
            id: data.policyId,
            agencyCallBackRate: !data.agencyCBPay ? 0 : data.agencyCBPay,
            agencyOnCallRate: !data.agencyCallPay ? 0 : data.agencyCallPay,
            agencyChargeRate: !data.agencyChargePay ? 0 : data.agencyChargePay,
            agencyDTRate: !data.agencyDTPay ? 0 : data.agencyDTPay,
            agencyHolidayRate: !data.agencyHOLPay ? 0 : data.agencyHOLPay,
            agencyOTRate: !data.agencyOTPay ? 0 : data.agencyOTPay,
            agencyRegularRate: !data.agencyRegPay ? 0 : data.agencyRegPay,
            agencyOnCall24HourRate: !data.agencyOnCall24HourPay ? 0 : data.agencyOnCall24HourPay,
            agencyWeekendRate: !data.agencyWeekendPay ? 0 : data.agencyWeekendPay,
            agencyNightCallRate: !data.agencyNightCallPay ? 0 : data.agencyNightCallPay,
            billDtRate: !data.billDoubleTimeRate ? 0 : data.billDoubleTimeRate,
            billOtRate: !data.billOverTimeRate ? 0 : data.billOverTimeRate,
            billRegularRate: !data.billRegRate ? 0 : data.billRegRate,
            billCallBackRate: !data.billCallBackRate ? 0 : data.billCallBackRate,
            billChargeRate: !data.billChargeRate ? 0 : data.billChargeRate,
            billOnCallRate: !data.billOnCallRate ? 0 : data.billOnCallRate,
            billHolidayRate: !data.billHolidayRate ? 0 : data.billHolidayRate,
            billNightCallRate: !data.billNightCallRate ? 0 : data.billNightCallRate,
            billOnCall24HourRate: !data.billOnCall24HourRate ? 0 : data.billOnCall24HourRate,
            billWeekendRate: !data.billWeekendRate ? 0 : data.billWeekendRate,
            oTDayRule: !data.billOverTimeDayRule ? 0 : data.billOverTimeDayRule,
            oTWeekRule: !data.billOverTimeWeekRule ? 0 : data.billOverTimeWeekRule,
            oT2WeekRule: !data.billOverTimeTwoWeekRule ? 0 : data.billOverTimeTwoWeekRule,
            dTDayRule: !data.billDoubleTimeDayRule ? 0 : data.billDoubleTimeDayRule,
            dTWeekRule: !data.billDoubleTimeWeekRule ? 0 : data.billDoubleTimeWeekRule,
            dT2WeekRule: !data.billDoubleTimeTwoWeekRule ? 0 : data.billDoubleTimeTwoWeekRule
        };
    }

    close(isSaved = false): void {
        this.dialogRef.close(isSaved);
    }

    isBillSection(section): boolean {
        return this._effectiveDatesService.isBillSection(section);
    }

    toFormattedDate(event) {
        this.firsttimeSaveFromEffectiveDate = false;

        const value = event.targetElement.value;
        if (!value.match(/\//g)) {
            this.startDateControl.patchValue('');
            return;
        }
        if (value.match(/\//g).length !== 1) {
            return;
        }

        const fullDate = new Date(value);
        const month = fullDate.getMonth() + 1;
        const day = fullDate.getDate();
        const currentYear = new Date().getFullYear();
        const formattedDate = new Date(`${month}/${day}/${currentYear}`);

        this.startDateControl.patchValue(formattedDate);
    }
}
